import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AssignmentCancellationPostModel } from '../components/details/models/assignment-cancellation-post.model';
import { AssignmentCancellationResultModel } from '../components/details/models/assignment-cancellation-result.model';
import { AssignmentSubstituteSearchCancellationResultModel } from '../components/details/models/assignment-substitute-search-cancellation-result.model';
import { AssignmentBillingPeriodDetailModel } from '../models/assignment-billing-period-detail.model';
import { AssignmentBillingPeriodModel } from '../models/assignment-billing-period.model';
import { AssignmentChangeSubstituteResultModel } from '../models/assignment-change-substitute-result.model';
import { AssignmentsFilterOptionsModel } from '../models/assignments-filter-options.model';
import { AssignmentsFilterModel } from '../models/assignments-filter.model';
import { AssignmentsOverviewModel } from '../models/assignments-overview.model';
import { AssignmentSubstituteModel } from '../models/assignments-substitute.model';
import { AssignmentsTakeoverPostModel } from '../models/assignments-takeover-post.model';

@Injectable({
    providedIn: 'root',
})
export class AssignmentsService {
    constructor(private httpClient: HttpClient) { }

    getAssignmentsFilterOptions(): Observable<AssignmentsFilterOptionsModel> {
        return this.httpClient.get<AssignmentsFilterOptionsModel>(`${environment.api}/assignments/filter/options`);
    }

    getPastAssignments(filterModel: AssignmentsFilterModel | null): Observable<AssignmentsOverviewModel[]> {
        const params = filterModel != null ? new HttpParams({ fromObject: this.buildQueryParams(filterModel) }) : new HttpParams();
        return this.httpClient.get<AssignmentsOverviewModel[]>(`${environment.api}/assignments/my/past`, { params })
            .pipe(map(assignments => assignments.map(a => ({ ...a, uniqueRandomId: crypto.randomUUID() }))));
    }

    getAssignmentBillingPeriods(year: number): Observable<AssignmentBillingPeriodModel[]> {
        return this.httpClient.get<AssignmentBillingPeriodModel[]>(
            `${environment.api}/assignments/my/billingPeriods?year=${year}`);
    }

    getAssignmentBillingPeriodDetails(periodId: number): Observable<AssignmentBillingPeriodDetailModel> {
        return this.httpClient.get<AssignmentBillingPeriodDetailModel>(`${environment.api}/assignments/my/billingPeriods/${periodId}`);
    }

    getUpcomingAssignments(filterModel: AssignmentsFilterModel | null): Observable<AssignmentsOverviewModel[]> {
        const params = filterModel != null ? new HttpParams({ fromObject: this.buildQueryParams(filterModel) }) : new HttpParams();
        return this.httpClient.get<AssignmentsOverviewModel[]>(`${environment.api}/assignments/my`, { params })
            .pipe(map(assignments => assignments.map(a => ({ ...a, uniqueRandomId: crypto.randomUUID() }))));
        ;
    }

    getOpenAssignments(filterModel: AssignmentsFilterModel | null): Observable<AssignmentsOverviewModel[]> {
        const params = filterModel != null ? new HttpParams({ fromObject: this.buildQueryParams(filterModel) }) : new HttpParams();
        return this.httpClient.get<AssignmentsOverviewModel[]>(`${environment.api}/assignments/open`, { params })
            .pipe(map(assignments => assignments.map(a => ({ ...a, uniqueRandomId: crypto.randomUUID() }))));
    }

    getTopThreeNextAssignments(): Observable<AssignmentsOverviewModel[]> {
        return this.httpClient.get<AssignmentsOverviewModel[]>(`${environment.api}/assignments/my?take=3`)
            .pipe(map(assignments => assignments.map(a => ({ ...a, uniqueRandomId: crypto.randomUUID() }))));
    }

    getTopThreeOpenAssignments(): Observable<AssignmentsOverviewModel[]> {
        return this.httpClient.get<AssignmentsOverviewModel[]>(`${environment.api}/assignments/open?take=3`)
            .pipe(map(assignments => assignments.map(a => ({ ...a, uniqueRandomId: crypto.randomUUID() }))));
    }

    sendTakeoverAssignments(takeoverAssignments: AssignmentsTakeoverPostModel): Observable<AssignmentChangeSubstituteResultModel[]> {
        return this.httpClient.post<AssignmentChangeSubstituteResultModel[]>(`${environment.api}/assignments/takeOver`, takeoverAssignments);
    }

    sendSubstituteAssignments(substituteAssignments: AssignmentSubstituteModel): Observable<AssignmentChangeSubstituteResultModel[]> {
        return this.httpClient.post<AssignmentChangeSubstituteResultModel[]>(`${environment.api}/assignments/setSubstitute`, substituteAssignments);
    }

    cancelAssignment(assignmentId: number, assignmentCancellation: AssignmentCancellationPostModel): Observable<AssignmentCancellationResultModel> {
        return this.httpClient.post<AssignmentCancellationResultModel>(`${environment.api}/assignments/${assignmentId}/cancel`, assignmentCancellation);
    }

    cancelAssignmentSubstitudeSearch(assignmentId: number): Observable<AssignmentSubstituteSearchCancellationResultModel> {
        return this.httpClient.post<AssignmentSubstituteSearchCancellationResultModel>(`${environment.api}/assignments/${assignmentId}/substituteSearch/cancel/`, null);
    }

    private buildQueryParams(filterModel?: AssignmentsFilterModel): { [param: string]: string | boolean } {
        const params: { [key: string]: string | boolean } = {};

        if (!filterModel) return params;

        if (filterModel.dateFrom) params['From'] = filterModel.dateFrom;
        if (filterModel.dateTo) params['To'] = filterModel.dateTo;
        if (filterModel.searchText) params['Search'] = encodeURIComponent(filterModel.searchText.substring(0, 50));
        // Commented out as the levle filter is incorporated within the search
        // if (filterModel.skillLevels) params['LevelIds'] = filterModel.skillLevels.flat().map(x => x.id).join(',');

        if (filterModel.weekdays && filterModel.isWeekdayFilterActive) {
            if (filterModel.weekdays.isMondayActive) params['Mo'] = true;
            if (filterModel.weekdays.isTuesdayActive) params['Tu'] = true;
            if (filterModel.weekdays.isWednesdayActive) params['We'] = true;
            if (filterModel.weekdays.isThursdayActive) params['Th'] = true;
            if (filterModel.weekdays.isFridayActive) params['Fr'] = true;
            if (filterModel.weekdays.isSaturdayActive) params['Sa'] = true;
            if (filterModel.weekdays.isSundayActive) params['Su'] = true;
        }
        return params;
    }
}
